import { ComponentType, useState } from 'react'
import {
  Box,
  Card,
  CardContent,
  CardHeader,
  Skeleton,
  Stack,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from '@mui/material'
import {
  CanonicalPeriod,
  canonicalPeriodLabels,
  EmissionTier,
  MetricState,
  metricStateLabels,
  PERIODS,
} from '../../climateAnalytics/common'
import { usePropertyClimateData, type PropertyResult } from '../../../hooks/climateData'
import { FloodScoreBadge } from '../../climateAnalytics/common/flood/utils'
import { ExtremeWindScoreBadge } from '../../climateAnalytics/common/extremeWind/utils'
import { HeatStressScoreBadge } from '../../climateAnalytics/common/heatStress/utils'
import { WildfireScoreBadge } from '../../climateAnalytics/common/fireRisk/utils'
import { WaterRiskScoreBadge } from '../../climateAnalytics/common/waterRisk/utils'

type ScoreBadgeComponent = ComponentType<{ score: number | null; size?: any }>

type HazardConfig = {
  key: string
  label: string
  badge: ScoreBadgeComponent
  getScore: (result: PropertyResult | undefined, tier: EmissionTier, period: CanonicalPeriod) => number | null
}

function parseNumericValue(node: any): number | null {
  if (node == null) return null
  if (typeof node === 'number') return Number.isNaN(node) ? null : node
  if (typeof node === 'string') {
    const parsed = Number.parseFloat(node)
    return Number.isNaN(parsed) ? null : parsed
  }
  if (Array.isArray(node)) {
    if (!node.length) return null
    return parseNumericValue(node[0]?.y ?? node[0])
  }
  if (typeof node === 'object') {
    if (typeof (node as any).y !== 'undefined') return parseNumericValue((node as any).y)
    if (typeof (node as any).value !== 'undefined') return parseNumericValue((node as any).value)
  }
  return null
}

function extractPeriodScore(
  result: PropertyResult | undefined,
  emissionTier: EmissionTier,
  period: CanonicalPeriod,
): number | null {
  if (!result || result.state?.value !== MetricState.SUCCEEDED) return null
  const tierData = result.data?.data?.[emissionTier]
  if (!tierData) return null
  const point = (tierData as any)[period] ?? tierData
  return parseNumericValue(point)
}

function extractScalarScore(result: PropertyResult | undefined, emissionTier: EmissionTier): number | null {
  if (!result || result.state?.value !== MetricState.SUCCEEDED) return null
  const tierData = result.data?.data?.[emissionTier]
  return parseNumericValue(tierData)
}

function extractWaterRiskCategory(
  result: PropertyResult | undefined,
  emissionTier: EmissionTier,
  period: CanonicalPeriod,
): number | null {
  if (!result || result.state?.value !== MetricState.SUCCEEDED) return null
  const tierData = result.data?.data?.[emissionTier]
  if (!tierData) return null
  return parseNumericValue((tierData as any).category?.[period] ?? tierData?.category)
}

const emissionControls = [
  { value: EmissionTier.LOW, label: 'Low' },
  { value: EmissionTier.MODERATE, label: 'Medium' },
  { value: EmissionTier.HIGH, label: 'High' },
]

const hazards: HazardConfig[] = [
  {
    key: 'flood',
    label: 'Flood',
    badge: FloodScoreBadge,
    getScore: extractPeriodScore,
  },
  {
    key: 'wildfire',
    label: 'Wildfire',
    badge: WildfireScoreBadge,
    getScore: extractPeriodScore,
  },
  {
    key: 'heat',
    label: 'Heat Stress',
    badge: HeatStressScoreBadge,
    getScore: extractPeriodScore,
  },
  {
    key: 'water',
    label: 'Water Stress',
    badge: WaterRiskScoreBadge,
    getScore: extractWaterRiskCategory,
  },
  {
    key: 'wind',
    label: 'Extreme Wind',
    badge: ExtremeWindScoreBadge,
    getScore: (result, tier) => extractScalarScore(result, tier),
  },
]
// TODO: Sort hazards by highest to lowest score, it's fine to sort it by raw scores.

const PropertyClimateHazardsCard = ({ propertyId }: { propertyId?: number | string }) => {
  const propertyIdNumber = typeof propertyId === 'number' ? propertyId : Number(propertyId)

  const [emissionTier, setEmissionTier] = useState<EmissionTier>(EmissionTier.HIGH)
  const [period, setPeriod] = useState<CanonicalPeriod>(CanonicalPeriod.MID_TERM)

  const floodQuery = usePropertyClimateData('fld:score', { propertyId: propertyIdNumber })
  const wildfireQuery = usePropertyClimateData('wf:score', { propertyId: propertyIdNumber })
  const heatQuery = usePropertyClimateData('hs:heat_scores', { propertyId: propertyIdNumber })
  const waterQuery = usePropertyClimateData('wr:ws_info', { propertyId: propertyIdNumber })
  const windQuery = usePropertyClimateData('tc:score', { propertyId: propertyIdNumber })

  const hazardQueries: Record<string, ReturnType<typeof usePropertyClimateData>> = {
    flood: floodQuery,
    wildfire: wildfireQuery,
    heat: heatQuery,
    water: waterQuery,
    wind: windQuery,
  }

  const hazardRows = hazards.map(hazard => {
    const query = hazardQueries[hazard.key]
    return {
      ...hazard,
      query,
      score: hazard.getScore(query?.data, emissionTier, period),
    }
  })

  const allLoading = hazardRows.every(hazard => hazard.query.isLoading && !hazard.query.data)

  return (
    <Card>
      <CardHeader title="Climate Risk Overview" />
      <CardContent>
        <Stack spacing={2.5}>
          {/* <Stack spacing={1.5}>
            <Box display="flex" alignItems="center" gap={2} flexWrap="wrap">
              <Typography variant="subtitle2" color="text.secondary" sx={{ minWidth: 120 }}>
                Emission tier
              </Typography>
              <ToggleButtonGroup
                size="small"
                value={emissionTier}
                exclusive
                onChange={(_, value) => value && setEmissionTier(value)}>
                {emissionControls.map(option => (
                  <ToggleButton key={option.value} value={option.value}>
                    {option.label}
                  </ToggleButton>
                ))}
              </ToggleButtonGroup>
            </Box>

            <Box display="flex" alignItems="center" gap={2} flexWrap="wrap">
              <Typography variant="subtitle2" color="text.secondary" sx={{ minWidth: 120 }}>
                Time horizon
              </Typography>
              <ToggleButtonGroup
                size="small"
                value={period}
                exclusive
                onChange={(_, value) => value && setPeriod(value)}>
                {PERIODS.map(option => (
                  <ToggleButton key={option} value={option}>
                    {canonicalPeriodLabels[option]}
                  </ToggleButton>
                ))}
              </ToggleButtonGroup>
            </Box>
          </Stack> */}

          <Stack spacing={1.25}>
            {hazardRows.map(hazard => {
              const state = hazard.query.data?.state?.value as MetricState | undefined
              const isLoading = hazard.query.isLoading || hazard.query.isFetching
              const statusText =
                state && state !== MetricState.SUCCEEDED
                  ? metricStateLabels[state] ?? 'Data unavailable'
                  : hazard.query.error
                  ? 'Unable to load data'
                  : 'No data for this selection'

              return (
                <Box
                  key={hazard.key}
                  display="flex"
                  alignItems="center"
                  justifyContent="space-between"
                  gap={2}
                  sx={{ py: 0.75, borderBottom: '1px solid', borderColor: 'divider' }}>
                  <Typography variant="subtitle2">{hazard.label}</Typography>
                  <Box>
                    {isLoading && !hazard.query.data ? (
                      <Skeleton variant="text" width={80} height={24} />
                    ) : hazard.score != null ? (
                      <hazard.badge score={hazard.score} size="dense" />
                    ) : (
                      <Typography variant="body2" color="text.secondary">
                        {statusText}
                      </Typography>
                    )}
                  </Box>
                </Box>
              )
            })}
          </Stack>
          <Typography variant="body2" color="text.secondary">
            For Period: 2050; Emissions: High
          </Typography>
        </Stack>

        {allLoading && (
          <Box display="flex" justifyContent="center" mt={2}>
            <Skeleton variant="rectangular" width="100%" height={64} />
          </Box>
        )}
      </CardContent>
    </Card>
  )
}

export default PropertyClimateHazardsCard
